import * as React from 'react';
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { cx } from '../utils';
import type { UnsafeProps } from '../escape-hatches';

export type ButtonIntent = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'tinted' | 'theme';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/** @deprecated Use `ButtonIntent` instead. */
export type ButtonVariant = ButtonIntent;

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'>, UnsafeProps {
  /** @deprecated Use `intent` instead. */
  variant?: ButtonIntent;
  intent?: ButtonIntent;
  size?: ButtonSize;
  className?: string;
  /** When true, sets `aria-busy="true"`, disables click, and shows a spinner. */
  loading?: boolean;
  /** When true, the button stretches to fill its container. */
  fullWidth?: boolean;
  /** Icon rendered before the label. */
  leadingIcon?: ReactNode;
  /** Icon rendered after the label. */
  trailingIcon?: ReactNode;
  /** Render as child element (Radix slot pattern). Only Button supports this. */
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    variant,
    intent,
    size = 'md',
    type = 'button',
    className,
    unsafe_className,
    unsafe_style,
    asChild,
    loading,
    fullWidth,
    leadingIcon,
    trailingIcon,
    onClick,
    ...rest
  },
  ref,
) {
  const resolvedIntent = intent ?? variant ?? 'primary';

  const classes = cx(
    'j-btn',
    `j-btn-${size}`,
    `j-btn-${resolvedIntent}`,
    fullWidth && 'j-btn-full',
    className,
    unsafe_className,
  );

  const handleClick = loading ? undefined : onClick;

  const ariaProps = loading ? { 'aria-busy': true as const } : {};

  const content = (
    <>
      {leadingIcon && <span className="j-btn-icon">{leadingIcon}</span>}
      <span className="j-btn-label">{children}</span>
      {loading && (
        <span className="j-btn-spinner" aria-hidden="true">
          <svg width="1em" height="1em" viewBox="0 0 16 16" fill="none">
            <circle
              cx="8"
              cy="8"
              r="6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="28"
              strokeDashoffset="8"
            />
          </svg>
        </span>
      )}
      {trailingIcon && <span className="j-btn-icon">{trailingIcon}</span>}
    </>
  );

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
      ...rest,
      ...ariaProps,
      onClick: handleClick,
      className: cx((children.props as { className?: string }).className, classes),
      style: {
        ...(children.props as { style?: CSSProperties }).style,
        ...unsafe_style,
      },
      ref,
    });
  }

  return (
    <button
      {...rest}
      {...ariaProps}
      ref={ref}
      type={type}
      className={classes}
      style={unsafe_style}
      onClick={handleClick}
    >
      {content}
    </button>
  );
});
